// product-page.js — THRUX product buy box / subscription picker interactivity.
//
// Drives the config-driven 1/3/6/10 picker (2026-09-09, C2 pricing + picker build).
// All math comes from the pure, Node-tested model in assets/thrux-pricing-model.js
// (window.ThruxPricing). Prices come ONLY from the thrux.pack_pricing shop metafield
// (rendered into a [data-pack-pricing] JSON block); no dollar figure is hardcoded.
//
// Each buy box owns its own state {cans, purchaseType, planId, mix}. When the config
// is absent the snippet renders a minimal "basic" buy box (single variant add at the
// live variant price); this script wires that path too so the product stays buyable.
//
// Cart submit: build an items[] payload from the flavor mix (selling_plan on EVERY
// line when subscribing, none on one-time), POST /cart/add.js with the real cart
// section ids, dispatch cart:update (Horizon's drawer opens), fire the gtag
// add_to_cart, and fall back to a native submit only when the add itself failed.
//
// Dev aid: set window.__thruxPackPricingFixture (an object of the metafield shape)
// BEFORE this script runs to preview the picker before the store metafield exists.
// Production never sets it.

(function () {
  'use strict';

  var P = window.ThruxPricing;

  function money(cents) {
    if (cents == null || isNaN(cents)) return '';
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  // --- cadence helpers (plan name -> interval; never hardcode a plan id) ---
  function planInterval(name) {
    var n = String(name || '').toLowerCase();
    var m = n.match(/every\s+(\d+)\s+month/);
    if (m) return parseInt(m[1], 10);
    if (/(every\s+month|monthly|1\s+month)/.test(n)) return 1;
    var m2 = n.match(/(\d+)\s+month/);
    if (m2) return parseInt(m2[1], 10);
    return 99; // unknown cadence sorts last
  }
  function cadenceLabel(interval) {
    if (interval === 1) return 'Every month';
    if (interval >= 99) return 'Custom schedule';
    return 'Every ' + interval + ' months';
  }
  function schedulePhrase(interval) {
    if (interval === 1) return 'month';
    if (interval >= 99) return 'delivery';
    return interval + ' months';
  }

  function getConfig(buyBox) {
    var raw = null;
    var el = buyBox.querySelector('[data-pack-pricing]');
    if (el && el.textContent) { try { raw = JSON.parse(el.textContent); } catch (e) { raw = null; } }
    if ((raw == null) && window.__thruxPackPricingFixture) raw = window.__thruxPackPricingFixture;
    return P ? P.parseConfig(raw) : null;
  }

  function readJSON(buyBox, sel) {
    var el = buyBox.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // --- flavor-carry intent (2026-09-10) -------------------------------------
  // The home flavor cards / chip row and a ?flavor=/?pack= deep link all express the
  // SAME intent: preselect a flavor preset (and optionally a pack size) in the picker.
  // They funnel through buyBox.thruxApplyIntent (set per picker in hydratePicker) via
  // a single 'thrux:apply-intent' document event, so callers never touch the picker's
  // internals. Purely a UI preselect — no store write, no typed price. Unknown values
  // are ignored (never over-select).
  function normalizePreset(kind) {
    kind = String(kind || '').toLowerCase().trim();
    if (kind === 'variety' || kind === 'mix') return 'variety';
    if (kind === 'cider' || kind === 'redline' || kind === 'neon') return kind;
    return null;
  }

  // Deep-link intent, parsed ONCE at load: /products/thrux?flavor=redline (all redline),
  // ?pack=variety (variety), optional ?cans=6. pack=variety wins over a flavor= value.
  var URL_INTENT = (function () {
    try {
      var q = new URLSearchParams(window.location.search);
      var kind = null;
      var pack = normalizePreset(q.get('pack'));
      if (pack === 'variety') kind = 'variety';
      if (!kind) kind = normalizePreset(q.get('flavor'));
      var out = {};
      if (kind) out.preset = kind;
      var cans = q.get('cans');
      if (cans && /^\d+$/.test(cans)) out.cans = parseInt(cans, 10);
      return (out.preset || out.cans) ? out : null;
    } catch (e) { return null; }
  })();

  var intentListenerBound = false;
  function bindIntentListener() {
    if (intentListenerBound) return;
    intentListenerBound = true;
    document.addEventListener('thrux:apply-intent', function (e) {
      var d = (e && e.detail) || {};
      var sel = d.instance ? '[data-buy-box][data-instance="' + d.instance + '"]' : '[data-buy-box]';
      document.querySelectorAll(sel).forEach(function (b) {
        if (typeof b.thruxApplyIntent === 'function') b.thruxApplyIntent(d);
      });
    });
  }

  function init() {
    bindIntentListener();
    document.querySelectorAll('[data-buy-box]').forEach(hydrate);
    syncGalleryToCurrent();
  }

  function hydrate(buyBox) {
    if (buyBox.dataset.productReady === 'true') return;
    buyBox.dataset.productReady = 'true';

    var cfg = getConfig(buyBox);
    var hasPicker = !!buyBox.querySelector('[data-buy-box-tiers]');
    if (cfg && hasPicker) hydratePicker(buyBox, cfg);
    else hydrateBasic(buyBox);
  }

  // ---------------------------------------------------------------------------
  // FULL PICKER
  // ---------------------------------------------------------------------------
  function hydratePicker(buyBox, cfg) {
    var drift = buyBox.getAttribute('data-price-drift');
    if (drift) console.warn('[thrux] buy-box price drift (variant price vs config base; expected pre-release on the live product): ' + drift);
    var flavorMap = readJSON(buyBox, '[data-flavor-map]') || {};
    var flavorOrder = ['cider', 'redline', 'neon'];
    // OOS handling (acceptance case 4): read the flavor map's `available`. Unknown -> assume available
    // (never over-block); the cart add still rejects a truly OOS variant with no silent substitution.
    function flavorAvailable(f) {
      var e = flavorMap[f]; if (!e) return true;
      var a = e.available;
      return Array.isArray(a) ? a[0] !== false : a !== false;
    }
    function availableFlavors() { return flavorOrder.filter(flavorAvailable); }
    var form = buyBox.querySelector('[data-buy-box-form]');
    var cta = buyBox.querySelector('[data-buy-box-cta]');
    var ctaLabel = buyBox.querySelector('[data-cta-label]');
    var ctaPrice = buyBox.querySelector('[data-buy-box-cta-price]');
    var summaryTotal = buyBox.querySelector('[data-summary-total]');
    var summarySchedule = buyBox.querySelector('[data-summary-schedule]');
    var summarySavings = buyBox.querySelector('[data-summary-savings]');
    var summaryWas = buyBox.querySelector('[data-summary-was]');
    var mixStatus = buyBox.querySelector('[data-mix-status]');
    var counters = buyBox.querySelector('[data-counters]');

    var tiers = Array.prototype.slice.call(buyBox.querySelectorAll('[data-tier]'));
    var ptypeToggle = buyBox.querySelector('[data-ptype-toggle]');
    var ptypeCards = Array.prototype.slice.call(buyBox.querySelectorAll('[data-ptype]'));
    var cadenceWrap = buyBox.querySelector('[data-cadence]');
    var cadenceOpts = Array.prototype.slice.call(buyBox.querySelectorAll('[data-cadence-opt]'));
    var presets = Array.prototype.slice.call(buyBox.querySelectorAll('[data-preset]'));
    var customizeBtn = buyBox.querySelector('[data-customize]');
    var counterRows = Array.prototype.slice.call(buyBox.querySelectorAll('[data-counter]'));

    var state = {
      cans: parseInt(buyBox.getAttribute('data-default-cans'), 10) || 6,
      purchaseType: 'onetime',
      interval: null,   // selected cadence in months (config v2)
      planId: null,     // resolved selling plan id (v2: tiers[cans].plans[interval]; v1: the pill's data-plan-id)
      v1PlanId: null,   // the selected pill's own plan id, for a v1 config (no plan map)
      mix: {}
    };

    // Resolve the selling plan for the current (tier, interval): config v2 maps it per tier x
    // interval; a v1 config carries the id on the pill itself (tier-independent). Called whenever the
    // tier or the cadence changes so the plan tracks the pack size.
    function resolvePlanId() {
      state.planId = P.planFor(cfg, state.cans, state.interval) || state.v1PlanId || null;
    }

    // ----- cadences: interval comes from data-interval (config v2) or is parsed from the plan name
    //       (v1); order monthly-first + relabel cleanly. The plan id is resolved per (tier, interval). -----
    cadenceOpts.forEach(function (opt) {
      var iv = opt.dataset.interval ? parseInt(opt.dataset.interval, 10) : planInterval(opt.getAttribute('data-plan-name'));
      opt.dataset.interval = iv;
      opt.textContent = cadenceLabel(iv);
    });
    cadenceOpts.sort(function (a, b) { return parseInt(a.dataset.interval, 10) - parseInt(b.dataset.interval, 10); });
    if (cadenceWrap) { var rg = cadenceWrap.querySelector('[role="radiogroup"]'); cadenceOpts.forEach(function (opt) { rg.appendChild(opt); }); }

    function selectedInterval() {
      var sel = cadenceOpts.filter(function (o) { return o.hasAttribute('data-selected'); })[0] || cadenceOpts[0];
      return sel ? parseInt(sel.dataset.interval, 10) : 1;
    }

    // ----- mix -----
    function applyPreset(kind) {
      var mix = {};
      var avail = availableFlavors();
      if (kind === 'variety') {
        // Fall back to the available flavors only (case 4). With >=1 available this always reaches N;
        // with none available the mix stays 0 -> invalid -> CTA blocked + the sold-out message.
        mix = P.presetMix(state.cans, avail.length ? avail : flavorOrder);
        flavorOrder.forEach(function (f) { if (mix[f] == null) mix[f] = 0; });
      } else {
        flavorOrder.forEach(function (f) { mix[f] = 0; });
        if (flavorAvailable(kind)) mix[kind] = state.cans;
      }
      state.mix = mix;
      renderCounters();
      renderMixStatus();
      updateCta();
    }
    function currentMixTotal() {
      var t = 0; flavorOrder.forEach(function (f) { t += state.mix[f] || 0; }); return t;
    }
    function renderCounters() {
      counterRows.forEach(function (row) {
        var f = row.getAttribute('data-flavor');
        var val = row.querySelector('[data-counter-value]');
        var dec = row.querySelector('[data-counter-dec]');
        var inc = row.querySelector('[data-counter-inc]');
        var n = state.mix[f] || 0;
        if (val) val.textContent = n;
        if (dec) dec.disabled = n <= 0;
        if (inc) inc.disabled = currentMixTotal() >= state.cans || !flavorAvailable(f);
      });
    }
    function renderMixStatus() {
      if (!mixStatus) return;
      if (!availableFlavors().length) {
        // Every flavor sold out: the mix can never reach N, so hold the CTA with a plain message.
        mixStatus.textContent = 'Every flavor is sold out right now. Please check back soon.';
        mixStatus.setAttribute('data-invalid', '');
        return;
      }
      var sel = currentMixTotal();
      var v = P.validateMix(state.mix, state.cans);
      mixStatus.textContent = sel + ' of ' + state.cans + ' cans selected';
      if (v.valid) mixStatus.removeAttribute('data-invalid');
      else mixStatus.setAttribute('data-invalid', '');
    }
    // Disable OOS flavors in the counters (+ their preset) and mark the row; called on init.
    function applyAvailabilityUI() {
      counterRows.forEach(function (row) {
        var f = row.getAttribute('data-flavor');
        if (!flavorAvailable(f)) {
          var inc = row.querySelector('[data-counter-inc]');
          if (inc) { inc.disabled = true; inc.setAttribute('aria-disabled', 'true'); }
          row.setAttribute('data-unavailable', '');
        }
      });
      presets.forEach(function (p) {
        var kind = p.getAttribute('data-preset');
        if (kind !== 'variety' && !flavorAvailable(kind)) { p.disabled = true; p.setAttribute('aria-disabled', 'true'); }
      });
    }
    function activePreset() {
      // reflect which preset (if any) matches the current mix
      presets.forEach(function (p) {
        var kind = p.getAttribute('data-preset');
        var match;
        if (kind === 'variety') {
          match = JSON.stringify(state.mix) === JSON.stringify(P.presetMix(state.cans, flavorOrder));
        } else {
          match = (state.mix[kind] || 0) === state.cans;
        }
        if (match) p.setAttribute('data-selected', ''); else p.removeAttribute('data-selected');
      });
    }

    // ----- price / summary / CTA -----
    function updateCta() {
      var total = P.tierTotalCents(cfg, state.cans, state.purchaseType);
      if (total == null) total = P.tierTotalCents(cfg, state.cans, 'onetime');
      if (summaryTotal) summaryTotal.textContent = money(total);
      if (ctaPrice) ctaPrice.textContent = money(total);

      var subscribing = state.purchaseType === 'subscribe';
      // Keep every tile's accessible name in sync with the purchase type + its price
      // (acceptance: labels/accessible names match the selected price and schedule).
      tiers.forEach(function (t) {
        var c = parseInt(t.getAttribute('data-cans'), 10);
        // A one-time-only pack (no subscribe price, e.g. the 15) keeps its one-time price in subscribe mode.
        var oto = t.hasAttribute('data-onetime-only');
        var useSub = subscribing && !oto;
        var cents = useSub ? parseInt(t.getAttribute('data-subscribe-cents'), 10) : parseInt(t.getAttribute('data-onetime-cents'), 10);
        // Single-can comparison (2026-10-24): the tile strikes the same cans at the single-can price.
        var wasC = parseInt(t.getAttribute('data-was-cents'), 10);
        var saveTxt = (wasC > cents) ? ', save ' + money(wasC - cents) + ' on single-can price' : '';
        t.setAttribute('aria-label', c + (c === 1 ? ' can, ' : ' cans, ') + money(cents) + (useSub ? ' per delivery' : (oto && subscribing ? ' one-time only' : ' one-time')) + saveTxt);
      });
      if (summaryWas) {
        var selTile = tiers.filter(function (t) { return parseInt(t.getAttribute('data-cans'), 10) === state.cans; })[0];
        var wasCents = selTile ? parseInt(selTile.getAttribute('data-was-cents'), 10) : NaN;
        if (wasCents > total) {
          summaryWas.textContent = money(wasCents);
          summaryWas.hidden = false;
        } else {
          summaryWas.hidden = true;
        }
      }
      if (ctaLabel) ctaLabel.textContent = subscribing ? 'SUBSCRIBE' : 'ADD TO CART';
      if (cta) cta.setAttribute('aria-label', (subscribing ? 'Subscribe to THRUX, ' : 'Add THRUX to cart, ') + state.cans + (state.cans === 1 ? ' can, ' : ' cans, ') + money(total));

      if (summarySchedule) {
        if (subscribing) {
          var iv = selectedInterval();
          summarySchedule.textContent = money(total) + ' today, then every ' + schedulePhrase(iv) + ' until canceled. Tax at checkout.';
          summarySchedule.hidden = false;
        } else {
          summarySchedule.hidden = true;
        }
      }
      if (summarySavings) {
        var save = P.savingsCents(cfg, state.cans);
        if (subscribing && save != null && save > 0) {
          summarySavings.textContent = 'Save ' + money(save) + ' on every delivery';
          summarySavings.hidden = false;
        } else {
          summarySavings.hidden = true;
        }
      }

      // Block add until exactly N cans are chosen (and, on subscribe, a plan exists).
      var v = P.validateMix(state.mix, state.cans);
      var ok = v.valid && (!subscribing || !!state.planId);
      if (cta) cta.disabled = !ok;
    }

    // ----- tier (pack) selection -----
    function selectTier(tierEl, opts) {
      opts = opts || {};
      tiers.forEach(function (t) { t.removeAttribute('data-selected'); t.setAttribute('aria-checked', 'false'); });
      tierEl.setAttribute('data-selected', ''); tierEl.setAttribute('aria-checked', 'true');
      state.cans = parseInt(tierEl.getAttribute('data-cans'), 10);
      // Pack size changed: show the resulting mix (preset), never silently discard.
      // Re-apply the "variety" preset for the new size unless an explicit single-flavor
      // preset is active, in which case keep that flavor at the new count.
      var singleKind = presets.filter(function (p) { return p.hasAttribute('data-selected') && p.getAttribute('data-preset') !== 'variety'; })[0];
      if (singleKind && !opts.forceVariety) applyPreset(singleKind.getAttribute('data-preset'));
      else applyPreset('variety');
      activePreset();
      resolvePlanId(); // config v2: the plan is per (tier, interval), so re-resolve when the pack changes
      // One-time-only pack (no subscription plan, e.g. the 15-can, 2026-09-23): picking it switches the
      // purchase type to one-time and locks the Subscribe card until a subscribable pack is picked again.
      var oneTimeOnly = tierEl.hasAttribute('data-onetime-only');
      setSubscribeLocked(oneTimeOnly, state.cans);
      if (oneTimeOnly && state.purchaseType === 'subscribe') selectPtype('onetime');
      updateCta();
      // gallery anchors to the first flavor with cans
      var firstF = flavorOrder.filter(function (f) { return (state.mix[f] || 0) > 0; })[0] || 'cider';
      if (flavorMap[firstF]) swapGalleryToVariant(flavorMap[firstF].id, buyBox);
    }

    tiers.forEach(function (t) {
      t.addEventListener('click', function () { selectTier(t); });
    });

    // ----- purchase type -----
    // The Subscribe card is locked (disabled + a note) while a one-time-only pack is selected.
    var subCard = ptypeCards.filter(function (c) { return c.getAttribute('data-ptype') === 'subscribe'; })[0] || null;
    var subNote = subCard ? subCard.querySelector('.product-buy-box__ptype-note') : null;
    var subNoteText = subNote ? subNote.textContent : '';
    function setSubscribeLocked(locked, cans) {
      if (!subCard) return;
      subCard.disabled = !!locked;
      if (locked) subCard.setAttribute('aria-disabled', 'true'); else subCard.removeAttribute('aria-disabled');
      if (subNote) subNote.textContent = locked ? 'Not available on the ' + cans + '-can pack.' : subNoteText;
    }
    function selectPtype(type) {
      if (type === 'subscribe' && subCard && subCard.disabled) return;
      state.purchaseType = type;
      buyBox.setAttribute('data-purchase-type', type);
      ptypeCards.forEach(function (c) {
        var on = c.getAttribute('data-ptype') === type;
        if (on) { c.setAttribute('data-selected', ''); c.setAttribute('aria-checked', 'true'); }
        else { c.removeAttribute('data-selected'); c.setAttribute('aria-checked', 'false'); }
      });
      if (cadenceWrap) cadenceWrap.hidden = (type !== 'subscribe');
      if (type === 'subscribe') {
        // default cadence = monthly (first, after sort) if none chosen
        if (!cadenceOpts.some(function (o) { return o.hasAttribute('data-selected'); }) && cadenceOpts[0]) {
          selectCadence(cadenceOpts[0]);
          return; // selectCadence calls updateCta
        }
      } else {
        state.planId = null;
      }
      updateCta();
    }
    ptypeCards.forEach(function (c) {
      c.addEventListener('click', function () { selectPtype(c.getAttribute('data-ptype')); });
    });

    // ----- cadence -----
    function selectCadence(opt) {
      cadenceOpts.forEach(function (o) { o.removeAttribute('data-selected'); o.setAttribute('aria-checked', 'false'); });
      opt.setAttribute('data-selected', ''); opt.setAttribute('aria-checked', 'true');
      state.interval = parseInt(opt.dataset.interval, 10);
      state.v1PlanId = opt.getAttribute('data-plan-id') || null;
      resolvePlanId();
      updateCta();
    }
    cadenceOpts.forEach(function (o) { o.addEventListener('click', function () { selectCadence(o); }); });

    // ----- presets + customize + counters -----
    presets.forEach(function (p) {
      p.addEventListener('click', function () {
        applyPreset(p.getAttribute('data-preset'));
        activePreset();
        var f = p.getAttribute('data-preset');
        if (f !== 'variety' && flavorMap[f]) swapGalleryToVariant(flavorMap[f].id, buyBox);
      });
    });
    if (customizeBtn && counters) {
      customizeBtn.addEventListener('click', function () {
        var open = counters.hidden;
        counters.hidden = !open;
        customizeBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    counterRows.forEach(function (row) {
      var f = row.getAttribute('data-flavor');
      var dec = row.querySelector('[data-counter-dec]');
      var inc = row.querySelector('[data-counter-inc]');
      if (inc) inc.addEventListener('click', function () {
        if (!flavorAvailable(f)) return;
        if (currentMixTotal() >= state.cans) return;
        state.mix[f] = (state.mix[f] || 0) + 1;
        renderCounters(); renderMixStatus(); activePreset(); updateCta();
        if (flavorMap[f]) swapGalleryToVariant(flavorMap[f].id, buyBox);
      });
      if (dec) dec.addEventListener('click', function () {
        if ((state.mix[f] || 0) <= 0) return;
        state.mix[f] = state.mix[f] - 1;
        renderCounters(); renderMixStatus(); activePreset(); updateCta();
      });
    });

    // ----- submit -----
    if (form) form.addEventListener('submit', function (e) { onSubmit(e, buyBox, cfg, flavorMap, flavorOrder, state, cta, form); });

    // ----- flavor-carry intent (scroll+preset from the cards / chips / deep link) -----
    // Preselect a pack size (optional) and a flavor preset. One-time stays the purchase
    // type (never silently enrolls a subscription). Blocked flavors (OOS) are ignored.
    function applyIntent(intent) {
      if (!intent) return;
      var cans = parseInt(intent.cans, 10);
      if (cans && !isNaN(cans)) {
        var tierEl = tiers.filter(function (t) { return parseInt(t.getAttribute('data-cans'), 10) === cans; })[0];
        if (tierEl) selectTier(tierEl, { forceVariety: true });
      }
      // Card/chip entry resets to one-time (the spec's default state); the deep link omits this, so a
      // ?flavor= link never yanks a shopper off a subscribe choice.
      if (intent.ptype === 'onetime') selectPtype('onetime');
      var kind = normalizePreset(intent.preset);
      if (kind && (kind === 'variety' || flavorAvailable(kind))) {
        applyPreset(kind);
        activePreset();
        if (kind !== 'variety' && flavorMap[kind]) swapGalleryToVariant(flavorMap[kind].id, buyBox);
      }
    }
    buyBox.thruxApplyIntent = applyIntent;

    // ----- initial state -----
    applyAvailabilityUI(); // disable OOS flavors before the default preset distributes over what is available
    var defaultTier = buyBox.querySelector('[data-tier][data-default="true"]') || tiers[0];
    if (defaultTier) selectTier(defaultTier, { forceVariety: true });
    selectPtype('onetime');
    renderCounters(); renderMixStatus(); activePreset(); updateCta();

    // Deep link (?flavor=/?pack=/?cans=) read once at load: preselect, no scroll.
    if (URL_INTENT) applyIntent(URL_INTENT);
  }

  function onSubmit(e, buyBox, cfg, flavorMap, flavorOrder, state, cta, form) {
    e.preventDefault();
    if (!cta || cta.disabled) return;
    var v = P.validateMix(state.mix, state.cans);
    if (!v.valid) return;

    var flavorToVariantId = {};
    flavorOrder.forEach(function (f) { if (flavorMap[f] && flavorMap[f].id) flavorToVariantId[f] = flavorMap[f].id; });
    var planId = state.purchaseType === 'subscribe' ? state.planId : null;
    var items = P.lineItems(flavorToVariantId, state.mix, planId);
    if (!items.length) return;

    var sectionsParam = cartSectionIds();
    cta.disabled = true; cta.classList.add('is-loading');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items, sections: sectionsParam })
    })
      .then(handleAddResponse)
      .then(function (added) {
        return refreshCart()
          .then(function (cart) { dispatchCartUpdate(cart, form, added && added.sections); })
          .catch(function (err) { console.warn('[thrux] add ok but refresh failed; redirecting', err); window.location.assign('/cart'); });
      }, function (err) {
        // Multi-line add failed: the form's items[] hidden inputs hold the no-JS default,
        // which does NOT match the chosen mix, so do NOT native-resubmit. Leave cart unchanged.
        console.warn('[thrux] picker add failed; cart unchanged', err);
      })
      .finally(function () { cta.disabled = false; cta.classList.remove('is-loading'); });
  }

  // ---------------------------------------------------------------------------
  // BASIC (no config) + WAITLIST: single-variant flavor pills + single add.
  // ---------------------------------------------------------------------------
  function hydrateBasic(buyBox) {
    var pills = Array.prototype.slice.call(buyBox.querySelectorAll('[data-variant-pill]'));
    var form = buyBox.querySelector('[data-buy-box-form]');
    var variantInput = buyBox.querySelector('[data-buy-box-variant-input]');
    var cta = buyBox.querySelector('[data-buy-box-cta]');

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        if (pill.disabled) return;
        pills.forEach(function (p) { p.removeAttribute('data-selected'); p.setAttribute('aria-checked', 'false'); });
        pill.setAttribute('data-selected', ''); pill.setAttribute('aria-checked', 'true');
        if (variantInput) variantInput.value = pill.dataset.variantId;
        swapGalleryToVariant(pill.dataset.variantId, buyBox);
      });
    });

    if (form && variantInput) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!cta || cta.disabled) return;
        var fd = new FormData(form);
        fd.append('sections', cartSectionIds());
        cta.disabled = true; cta.classList.add('is-loading');
        fetch('/cart/add.js', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' }, body: fd })
          .then(handleAddResponse)
          .then(function (added) {
            return refreshCart()
              .then(function (cart) { dispatchCartUpdate(cart, form, added && added.sections); })
              .catch(function () { window.location.assign('/cart'); });
          }, function (err) { fallbackSubmit(err, form); })
          .finally(function () { cta.disabled = false; cta.classList.remove('is-loading'); });
      });
    }
  }

  // ---------------------------------------------------------------------------
  // shared helpers (cart submit machinery + gallery swap)
  // ---------------------------------------------------------------------------
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) { if (el.dataset.sectionId) ids.push(el.dataset.sectionId); });
    return ids.join(',');
  }
  function handleAddResponse(r) { if (!r.ok) throw new Error('cart/add failed: ' + r.status); return r.json(); }
  function refreshCart() { return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }); }
  function dispatchCartUpdate(cart, form, sections) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: cart, sourceId: form && form.id, data: { source: 'product-page-buy-box', itemCount: cart.item_count, sections: sections } }
    }));
    if (window.Shopify && window.Shopify.customerPrivacy && window.Shopify.customerPrivacy.analyticsProcessingAllowed && window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true && typeof window.gtag === 'function') {
      var priceEl = form && form.querySelector('[data-buy-box-cta-price]');
      window.gtag('event', 'add_to_cart', { currency: 'USD', value: parseFloat(String(priceEl && priceEl.textContent || '0').replace('$', '')) || 0 });
    }
  }
  function fallbackSubmit(err, form) {
    console.warn('[thrux] buy box submit failed, native fallback', err);
    if (form) { var clone = form.cloneNode(true); form.parentNode.replaceChild(clone, form); clone.submit(); }
  }

  function swapGalleryToVariant(variantId, buyBox) {
    var mapEl = buyBox.querySelector('[data-buy-box-variant-images]');
    if (!mapEl) return;
    var entry;
    try { entry = JSON.parse(mapEl.textContent)[variantId]; } catch (e) { return; }
    if (!entry) return;
    var galleryImg = document.querySelector('[data-variant-media-target]');
    if (galleryImg && entry.url) { galleryImg.src = entry.url; galleryImg.removeAttribute('srcset'); if (entry.alt) galleryImg.alt = entry.alt; }
    toggleRedlineNote(entry.flavor);
  }
  function toggleRedlineNote(flavor) {
    var note = document.querySelector('[data-redline-note]');
    if (!note) return;
    if (flavor === 'redline') note.removeAttribute('hidden'); else note.setAttribute('hidden', '');
  }
  function syncGalleryToCurrent() {
    var heroBox = document.querySelector('[data-buy-box][data-instance="hero"]') || document.querySelector('[data-buy-box]');
    if (!heroBox) return;
    var sel = heroBox.querySelector('[data-variant-pill][data-selected]');
    if (sel) { swapGalleryToVariant(sel.dataset.variantId, heroBox); return; }
    var fmap = readJSON(heroBox, '[data-flavor-map]');
    if (fmap && fmap.cider && fmap.cider.id) swapGalleryToVariant(fmap.cider.id, heroBox);
  }

  // Image-defer gate (C2 2026-09-09, from tsr3's opener-window measurement): the buy-box
  // pack-tile + lid background-images (~155KB on DPR3) must NOT fetch while the LCP opener
  // is still downloading on slow 4G. product-page.css hides those backgrounds until
  // html.thrux-lcp-done. On the PDP we set that class on the opener's load event (so the
  // tile art paints right after the LCP, never competing with it); off the PDP (home,
  // athlete) there is no opener, so load them immediately. A 6s safety net guarantees the
  // tiles never stay blank. The tile boxes are reserved (padding), so no CLS either way.
  function gateLcpImages() {
    var html = document.documentElement;
    var done = function () { html.classList.add('thrux-lcp-done'); };
    var opener = document.querySelector('.pdp-gallery__opener');
    if (!opener || opener.complete) { done(); return; }
    opener.addEventListener('load', done, { once: true });
    opener.addEventListener('error', done, { once: true });
    setTimeout(done, 6000);
  }
  gateLcpImages();

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
